import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { usePhone } from "../../../shared/phone/media";
import { PhoneNav, type PhoneBack } from "../../../shared/phone/PhoneNav";
import { PnRow, Segmented } from "../../../shared/phone/Row";
import { ActionSheet } from "../../../shared/phone/Sheet";
import { Split } from "../../../shared/phone/Split";
import { Icon } from "../../components/ui/Icon";
import { api, ApiError } from "../../lib/api";
import { shownState, type ConnectionKind } from "../../lib/connectionsApi";
import type { ProductRef } from "../goal/FlowHead";
import { useToast } from "../goal/useToast";
import { useParentBack } from "../more/parentBack";
import { useSubScroll } from "../more/useSubScroll";
import { ConnectionDetail } from "./ConnectionDetail";
import { CX_ORDER } from "./CxRows";
import { CxSheetsNative } from "./CxSheetsNative";
import { PLACES, stateOf, type CxPlace } from "./cxNative";
import { SalesDetail } from "./SalesDetail";
import { useConnections } from "./useConnections";
import { useCxFlows } from "./useCxFlows";
import { useSalesTracking, type SalesTracking } from "./useSalesTracking";
import { Can } from "../../context/AuthContext";

const PLACE_KEYS: readonly CxPlace[] = ["meta", "email", "sales"];
/** Past this many products, a row with a list replaces the segmented control. */
const MOST_SEGMENTS = 3;

/**
 * Connections on a phone and an iPad: one row per connection, its state in colour and what it is for
 * under its name. A row opens the connection's own screen; on an iPad it opens beside the list.
 */
export function ConnectionsNative() {
  const { toast, say } = useToast();
  const [products, setProducts] = useState<ProductRef[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const sales = useSalesTracking(say);
  const [params] = useSearchParams();
  const phone = usePhone();
  const moreBack = useParentBack({ to: "/more", label: "More" });

  useEffect(() => {
    api.products.list().then(
      (r) => setProducts(r.products.map((p) => ({ id: p.id, name: p.name, logoR2Key: p.logoR2Key, coldEmail: p.coldEmail }))),
      (e) => setLoadError(e instanceof ApiError ? e.message : "The products did not load."),
    );
  }, []);

  const wanted = params.get("product");
  const product = products?.find((p) => p.id === wanted) ?? products?.[0] ?? null;

  if (!products || !product) {
    return (
      <div className="pn-screen">
        <PhoneNav title="Connections" large back={phone ? moreBack : undefined} />
        <div className="pn-stack">
          {loadError && <p className="mo-err mo-pad">{loadError}</p>}
          {products?.length === 0 && (
            <div className="pn-empty">
              <b>No product yet</b>
              Connections belong to a product.{" "}
              <Can min="editor">
                <Link to="/products/new">Add a product</Link>
              </Can>
            </div>
          )}
        </div>
      </div>
    );
  }
  return (
    <>
      <ProductCx key={product.id} product={product} products={products} sales={sales} say={say} moreBack={phone ? moreBack : undefined} />
      {toast}
    </>
  );
}

function ProductCx({
  product,
  products,
  sales,
  say,
  moreBack,
}: {
  product: ProductRef;
  products: ProductRef[];
  sales: SalesTracking;
  say: (message: string, done?: boolean) => void;
  moreBack: PhoneBack | undefined;
}) {
  const phone = usePhone();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [params] = useSearchParams();
  const { conns, error, reload } = useConnections(product.id);
  const f = useCxFlows({ productId: product.id, conns, reload, say });
  const reloadCx = useCallback(() => void reload(), [reload]);
  const [pickOpen, setPickOpen] = useState(false);

  const kinds: readonly ConnectionKind[] = product.coldEmail ? CX_ORDER : CX_ORDER.filter((k) => k !== "email");
  const asked = params.get("c") as CxPlace | null;
  const picked = asked && PLACE_KEYS.includes(asked) && (asked === "sales" || kinds.includes(asked)) ? asked : null;
  // Why: an iPad always has something beside the list; the first connection that exists, else Meta.
  const shown: CxPlace = picked ?? kinds.find((k) => shownState(f.byKind(k)) !== "none") ?? "meta";
  useSubScroll(phone ? picked : null);

  const base = `/connections?product=${encodeURIComponent(product.id)}`;
  const to = (c: CxPlace) => `${base}&c=${c}`;
  const back = phone ? { to: base, label: "Connections" } : undefined;

  const row = (k: CxPlace) => {
    const p = PLACES[k];
    const on = !phone && shown === k;
    if (k === "sales") {
      return sales.stripeConnected || !f.may ? (
        <PnRow key={k} to={to(k)} className={on ? "mo-cx on" : "mo-cx"} icon={<Icon name={p.icon} />} iconTone="violet" title={p.name} sub={p.for} wrap value={sales.stripeConnected ? <span className="mo-st good">Connected</span> : <span className="mo-st">Not connected</span>} />
      ) : (
        <PnRow key={k} to={to(k)} className={on ? "mo-cx on" : "mo-cx"} icon={<Icon name={p.icon} />} iconTone="violet" title={p.name} sub={p.for} wrap value={<span className="mo-cbtn">Connect</span>} chevron={false} />
      );
    }
    const c = f.byKind(k);
    if (shownState(c) === "none" && f.may) {
      // Why: on a phone Connect starts the flow at once; on an iPad the row opens the connection beside the list, with Connect there.
      return phone ? (
        <PnRow key={k} onClick={() => f.start(k)} className="mo-cx" icon={<Icon name={p.icon} />} iconTone="plain" title={p.name} sub={p.for} wrap value={<span className="mo-cbtn">Connect</span>} chevron={false} />
      ) : (
        <PnRow key={k} to={to(k)} className={on ? "mo-cx on" : "mo-cx"} icon={<Icon name={p.icon} />} iconTone="plain" title={p.name} sub={p.for} wrap value={<span className="mo-cbtn">Connect</span>} chevron={false} />
      );
    }
    const s = stateOf(c, f.busy === k);
    return <PnRow key={k} to={to(k)} className={on ? "mo-cx on" : "mo-cx"} icon={<Icon name={p.icon} />} title={p.name} sub={p.for} wrap value={<span className={`mo-st ${s.tone}`}>{s.words}</span>} />;
  };

  // Why: the product is a view of the same screen, so it keeps the screen's place in history and what opened it.
  const pick = (id: string) => navigate(`/connections?product=${encodeURIComponent(id)}`, { replace: true, state });

  const list = (
    <div className="pn-screen">
      <PhoneNav title="Connections" large back={moreBack} />
      <div className="pn-stack">
        {products.length > 1 && products.length <= MOST_SEGMENTS && (
          <Segmented value={product.id} options={products.map((p) => ({ value: p.id, label: p.name }))} onChange={pick} label="Product" />
        )}
        {products.length > MOST_SEGMENTS && (
          <div className="pn-grp">
            <PnRow icon={<Icon name="box" />} iconTone="plain" title="Product" value={product.name} onClick={() => setPickOpen(true)} chevron />
          </div>
        )}
        <section>
          {error && <p className="mo-err mo-pad">{error}</p>}
          <div className="pn-grp">
            {conns ? (
              [...kinds, "sales" as const].map(row)
            ) : (
              <div className="pn-row static">
                <span className="mo-spin" aria-hidden="true" />
                <span className="m">
                  <span className="t">Loading the connections…</span>
                </span>
              </div>
            )}
          </div>
          <p className="pn-note">Flashadz checks each one itself. Nothing is ticked by hand.</p>
        </section>
      </div>
      <ActionSheet open={pickOpen} onClose={() => setPickOpen(false)} title="Product" actions={products.map((p) => ({ label: p.name, onSelect: () => pick(p.id) }))} />
    </div>
  );

  const detail =
    shown === "sales" ? (
      <SalesDetail t={sales} say={say} back={back} may={f.may} />
    ) : (
      <ConnectionDetail product={product} kind={shown} conn={f.byKind(shown)} f={f} say={say} onReady={reloadCx} back={back} />
    );

  return (
    <>
      <Split list={list} detail={detail} showDetail={picked !== null} />
      <CxSheetsNative f={f} meta={f.byKind("meta")} />
    </>
  );
}
